import React from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useOrganization } from "../../../App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { LoadingScreen } from "@/components/ui/loading-spinner";
import { Lock } from "lucide-react";
import { NAV_ITEMS, isNavEnabled, navKeyForPath } from "./navItems";

/**
 * Blocks the pages of sidebar items the current organization has turned off, so hiding the
 * link is not the only gate. Platform admins see everything. A disabled Dashboard sends the
 * user to the first item still on; any other disabled page shows a locked card.
 */
export function NavGuard({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { currentOrganization, isLoading, isPlatformAdmin } = useOrganization();

  const key = navKeyForPath(pathname);
  if (!key) return <>{children}</>;

  // Only the first load: a later reload (org switch) keeps the page mounted
  if (isLoading && !currentOrganization) return <LoadingScreen text="Loading..." />;

  const flags = currentOrganization?.feature_flags;
  if (isPlatformAdmin || isNavEnabled(flags, key)) return <>{children}</>;

  const firstEnabled = NAV_ITEMS.find((item) => isNavEnabled(flags, item.key));

  if (key === "dashboard" && firstEnabled) {
    return <Navigate to={firstEnabled.href} replace />;
  }

  return (
    <div className="flex min-h-svh items-center justify-center p-4">
      <Card className="w-full max-w-md border shadow-sm">
        <CardContent className="flex flex-col items-center justify-center py-12 text-center">
          <Lock className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">Page Not Available</h3>
          <p className="text-muted-foreground mb-6">
            This page is not enabled for your organization. Please contact your
            administrator for access.
          </p>
          {firstEnabled && (
            <Button onClick={() => navigate(firstEnabled.href, { replace: true })}>
              Go to {firstEnabled.label}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
